import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getReimbursements, createReimbursement, reviewReimbursement, markReimbursementPaid,
  getCandidates, getDeployments
} from '../services/api';
import { Receipt, Plus, X, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';

const CLAIM_TYPES = ['medical','travel','meal','mobile','internet','uniform','other'];
const MONTHS      = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const curYear     = new Date().getFullYear();
const YEARS       = [curYear - 1, curYear, curYear + 1];
const FMT         = (n) => Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statusBadge = (s) =>
  s === 'paid'     ? 'bg-green-50 text-green-700' :
  s === 'approved' ? 'bg-blue-50 text-blue-700'   :
  s === 'rejected' ? 'bg-red-50 text-red-600'     :
  'bg-yellow-50 text-yellow-700';

const claimBadge = (t) => {
  const map = { medical: 'bg-rose-50 text-rose-700', travel: 'bg-sky-50 text-sky-700',
    meal: 'bg-orange-50 text-orange-700', mobile: 'bg-violet-50 text-violet-700',
    internet: 'bg-indigo-50 text-indigo-700', uniform: 'bg-teal-50 text-teal-700',
    other: 'bg-gray-50 text-gray-600' };
  return map[t] || 'bg-gray-50 text-gray-600';
};

// ── Create Claim Modal ─────────────────────
const CreateModal = ({ onClose }) => {
  const qc  = useQueryClient();
  const [form, setForm] = useState({
    candidate_id: '', deployment_id: '', claim_type: 'medical',
    amount: '', claim_date: new Date().toISOString().split('T')[0],
    description: '', bill_no: ''
  });

  const { data: candidates = [] } = useQuery({
    queryKey: ['cands-reim'],
    queryFn : () => getCandidates({ status: 'deployed', limit: 200 }).then(r => r.data.data || [])
  });

  const { data: deps = [] } = useQuery({
    queryKey: ['deps-reim', form.candidate_id],
    queryFn : () => form.candidate_id
      ? getDeployments({ candidate_id: form.candidate_id, status: 'active', limit: 10 }).then(r => r.data.data || [])
      : Promise.resolve([]),
    enabled: !!form.candidate_id
  });

  const mut = useMutation({
    mutationFn: () => createReimbursement({ ...form, amount: parseFloat(form.amount) }),
    onSuccess : () => { toast.success('Claim submitted'); qc.invalidateQueries(['reimbursements']); onClose(); },
    onError   : (e) => toast.error(e.response?.data?.message || 'Failed')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Submit Reimbursement Claim</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Employee *</label>
            <select value={form.candidate_id} onChange={e => setForm({...form, candidate_id: e.target.value, deployment_id: ''})} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select employee...</option>
              {candidates.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.employee_code})</option>)}
            </select>
          </div>

          {deps.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Deployment (optional)</label>
              <select value={form.deployment_id} onChange={e => setForm({...form, deployment_id: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select deployment...</option>
                {deps.map(d => <option key={d.id} value={d.id}>{d.clients?.company_name} — {d.designation}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Claim Type *</label>
              <select value={form.claim_type} onChange={e => setForm({...form, claim_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {CLAIM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount (₹) *</label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} min="1" required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Claim Date *</label>
              <input type="date" value={form.claim_date} onChange={e => setForm({...form, claim_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Bill / Receipt No.</label>
              <input value={form.bill_no} onChange={e => setForm({...form, bill_no: e.target.value})} placeholder="Optional"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2}
              placeholder="Brief description of the expense..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={() => mut.mutate()} disabled={mut.isPending || !form.candidate_id || !form.amount}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mut.isPending ? 'Submitting...' : 'Submit Claim'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Mark Paid Modal ───────────────────────
const MarkPaidModal = ({ claim, onClose }) => {
  const qc = useQueryClient();
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year,  setYear]  = useState(curYear);

  const mut = useMutation({
    mutationFn: () => markReimbursementPaid(claim.id, { month, year }),
    onSuccess : () => { toast.success('Marked as paid'); qc.invalidateQueries(['reimbursements']); onClose(); }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900">Mark as Paid</h2>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Paying ₹{FMT(claim.amount)} to {claim.candidates?.first_name} {claim.candidates?.last_name}.<br />
          Select the payroll month this will be included in:
        </p>
        <div className="flex gap-2 mb-4">
          <select value={month} onChange={e => setMonth(+e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(+e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
          <button onClick={() => mut.mutate()} disabled={mut.isPending}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
            {mut.isPending ? 'Processing...' : 'Mark Paid'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────
const Reimbursements = () => {
  const qc = useQueryClient();
  const [showAdd,   setShowAdd]   = useState(false);
  const [payingItem, setPayingItem] = useState(null);
  const [status,     setStatus]    = useState('');
  const [claimType,  setClaimType] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['reimbursements', status, claimType],
    queryFn : () => getReimbursements({ status: status || undefined, claim_type: claimType || undefined, limit: 100 }).then(r => r.data.data)
  });

  const reviewMut = useMutation({
    mutationFn: ({ id, approved }) => reviewReimbursement(id, { approved }),
    onSuccess : () => { toast.success('Updated'); qc.invalidateQueries(['reimbursements']); }
  });

  const items   = data?.items || [];
  const total   = items.reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const pending = items.filter(i => i.status === 'pending').reduce((s, i) => s + parseFloat(i.amount || 0), 0);
  const approved= items.filter(i => i.status === 'approved').reduce((s, i) => s + parseFloat(i.amount || 0), 0);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Reimbursements</h1>
          <p className="text-sm text-gray-400 mt-0.5">Employee expense claims — medical, travel, meal, and more</p>
        </div>
        <button onClick={() => setShowAdd(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={16} />
          New Claim
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-400 mb-1">Total Claims</p>
          <p className="text-2xl font-bold text-gray-900">₹{Number(Math.round(total)).toLocaleString('en-IN')}</p>
          <p className="text-xs text-gray-400 mt-1">{items.length} claim{items.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-400 mb-1">Pending Approval</p>
          <p className="text-2xl font-bold text-yellow-600">₹{Number(Math.round(pending)).toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 p-4">
          <p className="text-xs text-gray-400 mb-1">Approved (Unpaid)</p>
          <p className="text-2xl font-bold text-blue-600">₹{Number(Math.round(approved)).toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          {['pending','approved','rejected','paid'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={claimType} onChange={e => setClaimType(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Types</option>
          {CLAIM_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : !items.length ? (
          <div className="text-center py-12">
            <Receipt size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">No reimbursement claims yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{['Employee','Type','Amount','Date','Bill No.','Description','Status','Actions'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{r.candidates?.first_name} {r.candidates?.last_name}</p>
                    <p className="text-xs text-gray-400">{r.candidates?.employee_code}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${claimBadge(r.claim_type)}`}>
                      {r.claim_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">₹{FMT(r.amount)}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {new Date(r.claim_date).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">{r.bill_no || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{r.description || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(r.status)}`}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {r.status === 'pending' && (
                        <>
                          <button onClick={() => reviewMut.mutate({ id: r.id, approved: true })}
                            className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100">Approve</button>
                          <button onClick={() => reviewMut.mutate({ id: r.id, approved: false })}
                            className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100">Reject</button>
                        </>
                      )}
                      {r.status === 'approved' && (
                        <button onClick={() => setPayingItem(r)}
                          className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100">Pay</button>
                      )}
                      {r.status === 'paid' && (
                        <span className="text-xs text-gray-400">
                          Paid {r.paid_in_month ? `${MONTHS[r.paid_in_month-1]} ${r.paid_in_year}` : ''}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd    && <CreateModal onClose={() => setShowAdd(false)} />}
      {payingItem && <MarkPaidModal claim={payingItem} onClose={() => setPayingItem(null)} />}
    </div>
  );
};

export default Reimbursements;
